/**
 * Crisp volume-spike emoji (🐋 / 🦈 / 🐋🦈) on a chart canvas.
 *
 * The canvas context is shared by every primitive on the pane, so its state
 * (globalAlpha, fillStyle, shadow, filter) can arrive from whoever drew
 * before us. Chrome tints colour-emoji glyphs by the fill alpha, which is why
 * the markers used to come out as faint grey ghosts. Everything that affects
 * the glyph is therefore reset here, and each glyph sits on a small dark pill
 * with a light rim so it reads on candles, grid and either theme.
 *
 * Coordinates are media px; `hr` / `vr` are the bitmap pixel ratios
 * (devicePixelRatio-aware via lightweight-charts' bitmap space).
 */
export const EMOJI_FONT_STACK = '"Apple Color Emoji","Segoe UI Emoji","Noto Color Emoji","Twemoji Mozilla",sans-serif';

export interface BadgeStyle {
  /** Pill fill. */
  fill: string;
  /** Pill rim colour. */
  rim: string;
  /** Rim width, media px. */
  rimPx: number;
}

export const BADGE_STYLE: BadgeStyle = { fill: 'rgba(15, 23, 42, 0.78)', rim: 'rgba(248, 250, 252, 0.55)', rimPx: 1 };
/** 🐋🦈 (both venues agree): cyan rim, a touch heavier. */
export const BADGE_STYLE_BOTH: BadgeStyle = { fill: 'rgba(8, 47, 73, 0.85)', rim: '#38bdf8', rimPx: 1.5 };
/** Strong 🪝 wick rejection (level / spike / absorption): amber rim, same weight as 🐋🦈. */
export const BADGE_STYLE_WICK_STRONG: BadgeStyle = { fill: 'rgba(69, 26, 3, 0.85)', rim: '#f59e0b', rimPx: 1.5 };

/** Glyph scale for a pane `widthPx` wide: full size on desktop, gently smaller on a phone. */
export function emojiScale(widthPx: number): number {
  if (widthPx >= 600) return 1;
  if (widthPx <= 360) return 0.82;
  return 0.82 + ((widthPx - 360) / 240) * 0.18;
}

/** Pill size (media px) around a glyph of `fontPx` and measured width `glyphW`. */
export function badgeBox(fontPx: number, glyphW: number): { w: number; h: number } {
  const h = Math.round(fontPx * 1.3);
  return { w: Math.max(h, Math.round(glyphW + fontPx * 0.45)), h };
}

function pill(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.arc(x + w - r, y + r, r, -Math.PI / 2, Math.PI / 2);
  ctx.lineTo(x + r, y + h);
  ctx.arc(x + r, y + r, r, Math.PI / 2, (Math.PI * 3) / 2);
  ctx.closePath();
}

/** Reset every piece of inherited state that could dim or blur a glyph. */
export function resetGlyphState(ctx: CanvasRenderingContext2D): void {
  ctx.globalAlpha = 1;
  ctx.globalCompositeOperation = 'source-over';
  ctx.shadowColor = 'transparent';
  ctx.shadowBlur = 0;
  ctx.shadowOffsetX = 0;
  ctx.shadowOffsetY = 0;
  if ('filter' in ctx) (ctx as { filter: string }).filter = 'none';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
}

/** Draw `glyph` centred at media (x, y), `fontPx` tall, on a pill. */
export function drawEmojiBadge(
  ctx: CanvasRenderingContext2D,
  glyph: string,
  x: number,
  y: number,
  fontPx: number,
  hr: number,
  vr: number,
  style: BadgeStyle = BADGE_STYLE,
  /** Whole-badge opacity (pill and glyph together), e.g. 0.5 for an unconfirmed 🪝. */
  alpha = 1,
): void {
  resetGlyphState(ctx);
  ctx.globalAlpha = Math.min(1, Math.max(0, alpha));
  ctx.font = `${Math.round(fontPx * vr)}px ${EMOJI_FONT_STACK}`;
  const glyphW = ctx.measureText(glyph).width / hr;
  const { w, h } = badgeBox(fontPx, glyphW);
  pill(ctx, (x - w / 2) * hr, (y - h / 2) * vr, w * hr, h * vr, (h / 2) * Math.min(hr, vr));
  ctx.fillStyle = style.fill;
  ctx.fill();
  ctx.lineWidth = style.rimPx * hr;
  ctx.strokeStyle = style.rim;
  ctx.stroke();
  // Opaque fill: colour emoji are drawn at the fill's alpha.
  ctx.fillStyle = '#ffffff';
  ctx.fillText(glyph, x * hr, y * vr + fontPx * 0.06 * vr);
}
